import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { SessionTabs } from '../components/SessionTabs.js';

interface QuarantineRow {
  id: string;
  rowNumber: number;
  stage: string;
  reason: string;
  detail: Record<string, unknown> | null;
  sourceRow: Record<string, string>;
  createdAt: string;
}

export function QuarantinePage({ sessionId }: { sessionId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['quarantine', sessionId],
    queryFn: () => api.listQuarantine(sessionId),
    refetchInterval: 3000,
  });
  const [expanded, setExpanded] = useState<string | null>(null);
  const [stageFilter, setStageFilter] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string | null>(null);

  const rows = (data?.rows ?? []) as unknown as QuarantineRow[];

  // The worker formats a map/load reason as "<ResourceType>: <detail>"; pull the
  // type out so we can group by it. Falls back to "other" (e.g. extract errors).
  const parseType = (reason: string): string => reason.match(/^([A-Z][A-Za-z]+):/)?.[1] ?? 'other';
  const tally = (key: (r: QuarantineRow) => string): Array<[string, number]> => {
    const m = new Map<string, number>();
    for (const r of rows) m.set(key(r), (m.get(key(r)) ?? 0) + 1);
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  };
  const byStage = tally((r) => r.stage);
  const byType = tally((r) => parseType(r.reason));
  const shown = rows.filter(
    (r) => (!stageFilter || r.stage === stageFilter) && (!typeFilter || parseType(r.reason) === typeFilter),
  );

  return (
    <div className="space-y-4 max-w-5xl mx-auto p-6">
      <SessionTabs sessionId={sessionId} active="quarantine" />
      <div>
        <h1 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>Quarantine</h1>
        <div className="text-[12px]" style={{ color: 'var(--text-dimmed)' }}>
          {rows.length} row{rows.length === 1 ? '' : 's'}
        </div>
      </div>

      {rows.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-widest" style={{ color: 'var(--text-dimmed)' }}>Stage</span>
          {byStage.map(([s, n]) => (
            <button
              key={s}
              type="button"
              onClick={() => setStageFilter(stageFilter === s ? null : s)}
              className="rounded-full px-2 py-0.5 text-[11px] font-mono"
              style={{ background: stageFilter === s ? 'var(--accent-blue)22' : 'var(--bg-tertiary)', border: `1px solid ${stageFilter === s ? 'var(--accent-blue)' : 'var(--border)'}`, color: 'var(--text-secondary)' }}
            >
              {s} · {n}
            </button>
          ))}
          <span className="ml-2 text-[10px] uppercase tracking-widest" style={{ color: 'var(--text-dimmed)' }}>Type</span>
          {byType.map(([t, n]) => (
            <button
              key={t}
              type="button"
              onClick={() => setTypeFilter(typeFilter === t ? null : t)}
              className="rounded-full px-2 py-0.5 text-[11px]"
              style={{ background: typeFilter === t ? 'var(--accent-purple)22' : 'var(--bg-tertiary)', border: `1px solid ${typeFilter === t ? 'var(--accent-purple)' : 'var(--border)'}`, color: 'var(--text-secondary)' }}
            >
              {t} · {n}
            </button>
          ))}
          {(stageFilter || typeFilter) && (
            <button type="button" onClick={() => { setStageFilter(null); setTypeFilter(null); }} className="ml-1 text-[11px] underline" style={{ color: 'var(--text-dimmed)' }}>
              clear
            </button>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="text-[12px]" style={{ color: 'var(--text-dimmed)' }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-md p-4 text-[12px]" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-dimmed)' }}>
          No quarantined rows.
        </div>
      ) : shown.length === 0 ? (
        <div className="rounded-md p-4 text-[12px]" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-dimmed)' }}>
          No rows match the filter.
        </div>
      ) : (
        <div className="space-y-2">
          {shown.map((row) => {
            const isOpen = expanded === row.id;
            const stageColor =
              row.stage === 'extract' ? 'rgba(238,93,80,0.18)' :
              row.stage === 'map' ? 'rgba(250,204,21,0.18)' :
              row.stage === 'validate' ? 'rgba(13,207,184,0.18)' :
              row.stage === 'load' ? 'rgba(168,85,247,0.18)' :
              'rgba(148,163,184,0.18)';
            return (
              <div key={row.id} className="rounded-md overflow-hidden" style={{ border: '1px solid var(--border)' }}>
                <button
                  type="button"
                  onClick={() => setExpanded(isOpen ? null : row.id)}
                  className="w-full text-left px-3 py-2 flex items-center justify-between gap-3"
                  style={{ background: isOpen ? 'var(--bg-tertiary)' : 'var(--bg-secondary)' }}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded shrink-0" style={{ background: stageColor, color: 'var(--text-primary)' }}>
                      {row.stage}
                    </span>
                    <span className="text-[12px] truncate" style={{ color: 'var(--text-primary)' }}>row {row.rowNumber} — {row.reason}</span>
                  </div>
                  <span className="text-[10px]" style={{ color: 'var(--text-dimmed)' }}>{isOpen ? '▾' : '▸'}</span>
                </button>
                {isOpen && (
                  <div className="p-3 space-y-2" style={{ background: 'var(--bg-secondary)', borderTop: '1px solid var(--border)' }}>
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: 'var(--text-dimmed)' }}>Detail</div>
                      <pre className="rounded-md p-2 font-mono text-[11px] overflow-auto" style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}>
                        {row.detail ? JSON.stringify(row.detail, null, 2) : '(none)'}
                      </pre>
                    </div>
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: 'var(--text-dimmed)' }}>Source row</div>
                      <pre className="rounded-md p-2 font-mono text-[11px] overflow-auto" style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}>
                        {JSON.stringify(row.sourceRow, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
